// frontend/src/pages/modulos/ModulosListPage.jsx
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PermissionDeniedAlert from '../../components/common/PermissionDeniedAlert';
import AlertException from '../../components/common/AlertException';
import ProtectedPage from '../../components/common/ProtectedPage';
import { useAuth } from '../../contexts/AuthContext';

function ModulosListPage() {
  const { hasPermission } = useAuth();
  const [modulos, setModulos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current = controller;

    const fetchModulos = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get('/modulos', { signal: controller.signal });
        const payload = response?.data?.data || response?.data || [];
        setModulos(Array.isArray(payload) ? payload : []);
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err?.response?.status === 403) {
          setError({ kind: 'permission' });
        } else {
          setError(err);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchModulos();
    return () => controller.abort();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza de que deseja excluir este módulo?')) return;
    try {
      await api.delete(`/modulos/${id}`);
      setModulos((prev) => prev.filter((m) => m.id !== id));
    } catch (err) {
      if (err?.response?.status === 403) {
        alert('Acesso não permitido.');
      } else {
        alert('Ocorreu um erro ao excluir o módulo.');
      }
    }
  };

  const sorted = [...modulos].sort((a, b) => (a?.nome || '').localeCompare(b?.nome || ''));
  const filtered = sorted.filter((m) => {
    const nome = (m?.nome || '').toLowerCase();
    const unidade = (m?.unidade?.nome || '').toLowerCase();
    const term = searchTerm.toLowerCase();
    return nome.includes(term) || unidade.includes(term);
  });

  if (loading) {
    return (
      <div className="modulos-page">
        <LoadingSpinner text="Carregando módulos..." />
      </div>
    );
  }

  if (error) {
    if (error.kind === 'permission') {
      return (
        <div className="modulos-page">
          <PermissionDeniedAlert
            requiredPermission="config.modulos"
            action="listar módulos"
          />
        </div>
      );
    }
    return (
      <div className="modulos-page">
        <AlertException error={error} fallback="Não foi possível carregar os módulos." />
      </div>
    );
  }

  return (
    <ProtectedPage permission="config.modulos" action="visualizar a lista de módulos">
      <div className="modulos-page">
        <div className="content-header">
          <h1 className="content-title">Módulos</h1>
          <p className="content-subtitle">Lista de módulos cadastrados no sistema</p>
        </div>

        <div className="card modulos-list-card">
          <div className="card-body">
            {/* Barra de busca e botão de Adicionar */}
            <div className="list-toolbar d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
              <div className="input-group" style={{ maxWidth: '400px' }}>
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por nome ou unidade..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Link to="/painel/modulos/novo" className="btn btn-primary">
                <i className="bi bi-plus-lg me-2"></i>
                Novo Módulo
              </Link>
            </div>

            <div className="table-responsive">
              <table className="table table-dark-custom table-stack">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Nome</th>
                    <th>Unidade</th>
                    <th>Data de Criação</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length > 0 ? (
                    filtered.map((modulo) => (
                      <tr key={modulo.id}>
                        <td data-label="ID">{modulo.id}</td>
                        <td data-label="Nome">{modulo.nome}</td>
                        <td data-label="Unidade">{modulo.unidade ? modulo.unidade.nome : 'N/A'}</td>
                        <td data-label="Data de Criação">{modulo.created_at ? String(modulo.created_at).split(' ')[0] : '-'}</td>
                        <td className="table-actions modulos-table-actions d-flex gap-2" data-label="Ações">
                          <Link
                            to={`/painel/modulos/${modulo.id}/editar`}
                            className="btn btn-sm btn-outline-warning"
                            title="Editar"
                          >
                            <i className="bi bi-pencil"></i>
                          </Link>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            title="Excluir"
                            onClick={() => handleDelete(modulo.id)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">Nenhum módulo encontrado.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </ProtectedPage>
  );
}

export default ModulosListPage;
